<template>
    <LayoutBase @keyup.enter="onLogin">
        <template #description>
            {{ t("common.noAccount") }}
            <UiLink :href="registrationUrl">{{ t("common.register") }}</UiLink>
        </template>

        <AuthInput
            id="login-email"
            v-model.trim="email"
            :label="t('common.email')"
        />

        <AuthInput
            id="login-password"
            v-model="password"
            type="password"
            :label="t('common.password')"
        />

        <div class="row row_top-m" :class="$style['form-row']">
            <UiCheckboxField v-model:model="rememberMe" :label="t('rememberMe')" />
            <router-link v-slot="{ navigate }" custom :to="{ name: 'reminder' }">
                <UiLink
                    :class="$style['forget-link']"
                    href="#"
                    @click="navigate"
                >
                    {{ t("forgetPassword") }}
                </UiLink>
            </router-link>
        </div>

        <div class="row row_top-m">
            <UiButton
                :class="$style['full-width']"
                :disabled="isSending"
                size="md"
                @click="onLogin"
            >
                {{ t("signIn") }}
            </UiButton>
        </div>

        <template v-if="availableSocials.length">
            <div :class="$style['delimiter']">
                <div :class="$style['delimiter__line']" />
                {{ t("or") }}
                <div :class="$style['delimiter__line']" />
            </div>

            <div>
                <UiButton
                    v-for="social in availableSocials"
                    :key="social"
                    :class="[$style['available-socials-btn'], $style[`available-socials-btn_${social}`]]"
                    size="md"
                    @click="authWithSocial(social)"
                >
                    <IconYandex v-if="social === SocialProvider.YANDEX" />
                    {{ t("authWith") }} {{ socialName(social) }}
                </UiButton>
            </div>
        </template>

        <div v-if="successMessage" class="row row_top-m">
            <UiAlert
                :text="successMessage"
                type="success"
                class="alert"
            />
        </div>

        <div v-if="errorMessage || lastSocialError" class="row row_top-m">
            <UiAlert :text="errorTranslate" type="danger" />
        </div>
    </LayoutBase>
</template>

<script lang="ts" setup>
import AuthInput from '@/components/common/AuthInput.vue';
import LayoutBase from '@/components/layouts/LayoutBase.vue';

import IconYandex from '@/components/icons/yandex.svg?component';

import { useI18n } from 'vue-i18n';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';

import { useAuthStore } from '@/stores/auth';
import { usePasswordStore } from '@/stores/password';
import { useCaptcha } from '@/plugins/captcha';
import { useAnalytics } from '@/plugins/analytics';
import { useSending } from '@/composables/sending';
import { useSocialStore } from '@/stores/social';

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();
const { login } = useAuthStore()
const {
    availableSocials,
    initSocialLogin,
    lastSocialError,
    hideLastSocialError,
} = useSocialStore()
const { captcha } = useCaptcha();
const { makeRequestChangePassword, makeRequestResetPassword } = usePasswordStore();
const analytics = useAnalytics();
const { isSending, setSending } = useSending();

const enum SocialProvider {
    YANDEX = 'yandex',
}

const email = ref<string>('');
const password = ref<string>('');
const rememberMe = ref<boolean>(true);

const errorMessage = ref<string>('');

const allowedErrors = new Map<string, string>([
    ['Email is a required field', t('error.emptyEmail')],
    ['Password is a required field', t('error.emptyPassword')],
    ['Email must be a valid email address', t('error.notValidEmail')],
    ['invalid credentials', t('error.authError')],
    ['user connection not found', t('error.socialNotConnected')],
    ['authentication not requested', t('error.authenticationNotRequested')],
    ['authentication aborted by provider', t('error.abortedByProvider')],
])

onBeforeMount(async () => {
    hideLastSocialError()
    analytics.trackEvent('authLoginPageView')
})

const errorTranslate = computed((): string => {
    if (errorMessage.value) {
        return allowedErrors.get(errorMessage.value) ?? t('error.undefinedError')
    }

    if (lastSocialError) {
        return allowedErrors.get(lastSocialError) ?? t('error.undefinedError')
    }

    return ''
});

const successMessage = computed((): string => {
    if (makeRequestResetPassword) {
        return t('resetPassword');
    }

    if (makeRequestChangePassword) {
        return t('changePassword');
    }

    return '';
});

watch([email, password], () => {
    errorMessage.value = ''
})

const onLogin = () => {
    setSending(true)
    captcha.execute('login').then(function(token: string) {
        login(email.value, password.value, rememberMe.value, token)
            .then(({ error }) => {
                errorMessage.value = '';

                if (error != null) {
                    errorMessage.value = error.messages[0] ?? '';

                    return;
                }

                router.push({ name: 'main', query: route.query });
            })
            .finally(() => setSending(false));
    });
};

const registrationUrl = computed(() => {
    return import.meta.env.VITE_REGISTRATION_URL + '?lang=' + locale.value.split('_')[0]
})

const authWithSocial = (provider: string) => {
    initSocialLogin(provider)
        .then((authURL) => {
            if (authURL.length) {
                window.location.href = authURL
            }
        })
}

const socialName = (provider: string) => {
    switch (provider) {
        case SocialProvider.YANDEX:
            return t('authProvider.yandex')
        default:
            return ''
    }
}
</script>

<style lang="less" module>
@import (reference) "../assets/styles/variables.less";
@import (reference) "@omnica/base/assets/stylesheets/layout.less";
@import (reference) "@omnica/base/assets/stylesheets/palette.less";

.available-socials-btn {
    width: 100%;

    :global(.omnica-button__text) {
        font-weight: 600;
    }

    & + & {
        margin-top: @spacing-s;
    }

    &_yandex:global(.omnica-button) {
        background: black;

        &:active {
            background: white;
            color: black;
            outline: 1px solid black !important;
        }
    }
}

.delimiter {
    display: flex;
    align-items: center;
    padding: @spacing-s 0;
    gap: 10px;

    &__line {
        width: 100%;
        height: 1px;
        background: @grey-500;
    }
}

.form-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.forget-link {
  white-space: nowrap;
  margin-top: -1px;
}

.full-width {
  width: 100%;
}
</style>

<i18n locale="en_GB" lang="json">
{
    "forgetPassword": "Forget password?",
    "signIn": "Sign in",
    "rememberMe": "Remember me on this computer",
    "changePassword": "We sent an email with\u00a0instructions on how to reset the password",
    "resetPassword": "Password reset",
    "error": {
        "emptyEmail": "Enter your email",
        "emptyPassword": "Enter password",
        "notValidEmail": "Email entered is incorrect",
        "authError": "Incorrect email or password",
        "socialNotConnected": "The social network is not linked to any of the accounts",
        "authenticationNotRequested": "A connection was not requested or timed out",
        "abortedByProvider": "Authorization is blocked by the provider",
        "undefinedError": "Unknown error"
    },
    "or": "or",
    "authWith": "Login via",
    "authProvider": {
      "yandex": "Yandex ID"
    }
}
</i18n>

<i18n locale="es_ES" lang="json">
{
    "forgetPassword": "¿Has olvidado contraseña?",
    "signIn": "Entrar",
    "rememberMe": "Recordarme en este ordenador",
    "changePassword": "Hemos enviado el correo con\u00a0la instrucción para resetear la contraseña",
    "resetPassword": "Contraseña reseteada",
    "error": {
        "emptyEmail": "Introduce tu correo electrónico",
        "emptyPassword": "Introducir la contraseña",
        "notValidEmail": "El correo electrónico ingresado es incorrecto",
        "authError": "El email o la contraseña es incorrecta",
        "socialNotConnected": "La red social no está vinculada a ninguna de las cuentas.",
        "authenticationNotRequested": "No se solicitó una conexión o se agotó el tiempo de espera",
        "abortedByProvider": "La autorización está bloqueada por el proveedor",
        "undefinedError": "Error desconocido"
    },
    "or": "o",
    "authWith": "Iniciar sesión con",
    "authProvider": {
      "yandex": "Yandex ID"
    }
}
</i18n>

<i18n locale="ru_RU" lang="json">
{
    "forgetPassword": "Забыли пароль?",
    "signIn": "Войти",
    "rememberMe": "Запомнить меня",
    "changePassword": "Мы отправили письмо с\u00a0инструкцией как сбросить пароль",
    "resetPassword": "Пароль сброшен",
    "error": {
        "emptyEmail": "Введите почту",
        "emptyPassword": "Введите пароль",
        "notValidEmail": "Почта введена не верно",
        "authError": "Неверная почта или пароль",
        "socialNotConnected": "Соцсеть не привязана ни к одному из аккаунтов",
        "authenticationNotRequested": "Подключение не было запрошено или время ожидания истекло",
        "abortedByProvider": "Авторизация заблокирована со стороны провайдера",
        "undefinedError": "Неизвестная ошибка"
    },
    "or": "или",
    "authWith": "Войти с",
    "authProvider": {
      "yandex": "Яндекс ID"
    }
}
</i18n>
